import { useEffect, useRef, useState } from "react"

interface MaskDimensions {
  maskX: number
  maskY: number
  maskWidth: number
  maskHeight: number
  strokeWidth: number
  strokeMargin: number
}

/** Oval cut-out geometry, sized from the CONTAINER (origene CameraScreenV2 layout). */
function calculateMaskDimensions(width: number, height: number): MaskDimensions {
  const sideMargin = 32
  const topMargin = 88
  const bottomMargin = 33
  const buttonHeight = 80
  const buttonBottomMargin = 32

  const maskWidth = width - sideMargin * 2 + 1
  const maskHeight = height - topMargin - bottomMargin - buttonHeight - buttonBottomMargin

  return { maskX: sideMargin, maskY: topMargin, maskWidth, maskHeight, strokeWidth: 2, strokeMargin: 5 }
}

/**
 * Dimming with a rounded oval cut-out and a soft-shadowed outline — the origene
 * camera framing. Sized from its OWN container (not the viewport), so on web,
 * where the camera is constrained to the mobile column, the oval stays inside the
 * visible photo instead of stretching across the whole screen. Recomputes on resize.
 */
export function CameraMask() {
  const ref = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => setSize({ width: el.clientWidth, height: el.clientHeight })
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const d = calculateMaskDimensions(size.width, size.height)
  const radius = Math.min(d.maskWidth, d.maskHeight) * 0.5

  return (
    <div ref={ref} className="pointer-events-none absolute inset-0 z-10">
      {size.width > 0 && d.maskWidth > 0 && d.maskHeight > 0 && (
        <svg width="100%" height="100%">
          <defs>
            <mask id="camera-mask-hole">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              <rect
                x={d.maskX}
                y={d.maskY}
                width={d.maskWidth}
                height={d.maskHeight}
                rx={radius}
                ry={radius}
                fill="black"
              />
            </mask>
            <filter id="camera-mask-shadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="#f5f5f5" floodOpacity="0.7" />
            </filter>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="#040B1DCC" mask="url(#camera-mask-hole)" />
          <rect
            x={d.maskX - d.strokeMargin}
            y={d.maskY - d.strokeMargin}
            width={d.maskWidth + d.strokeMargin * 2}
            height={d.maskHeight + d.strokeMargin * 2}
            rx={radius + d.strokeMargin}
            ry={radius + d.strokeMargin}
            fill="none"
            stroke="#f5f5f5"
            strokeWidth={d.strokeWidth}
            filter="url(#camera-mask-shadow)"
          />
        </svg>
      )}
    </div>
  )
}
